'use client';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn, useMediaQuery } from '@sharpr/ui';
import { AnimatedSection } from '@/components/client/landing/AnimatedSection';
import { GLASS_CARD, SectionEyebrow, SectionTitle } from './shared';
import { NBA_RESULTS, type ResultsTier } from './nbaResults.generated';

type TierKey = keyof typeof NBA_RESULTS.tiers;
type SnapshotKey = keyof typeof NBA_RESULTS.snapshots;
const SNAPSHOT_ORDER: SnapshotKey[] = ['lpu', 'min30'];
// Phone labels: both filters share one row, so each segment gets ~60px of text.
const TIER_SHORT: Record<TierKey, string> = { edge10: '10%+', all: 'All +EV' };
const SNAPSHOT_SHORT: Record<SnapshotKey, string> = { lpu: 'Posted', min30: '30 min' };

// Headline tier first: the toggle defaults to it.
const TIER_ORDER: TierKey[] = ['edge10', 'all'];

// --- Formatting -----------------------------------------------------------------

const fmtUnits = (u: number, dp = 1) => `${u >= 0 ? '+' : '−'}${Math.abs(u).toFixed(dp)}u`;
const fmtPct = (f: number, dp = 1) => `${f >= 0 ? '+' : '−'}${Math.abs(f * 100).toFixed(dp)}%`;
const fmtInt = (n: number) => n.toLocaleString('en-US');

// Dates are ET calendar days; format in UTC so they never shift a day.
const fmtDate = (iso: string, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });

// --- Motion helpers ---------------------------------------------------------------

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return reduced;
}

/** Flips true once the element scrolls ~a third into view. */
function useInView<T extends Element>() {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView]);
  return { ref, inView };
}

/** Eases from the previous value to `target` whenever it changes (once active). */
function useCountUp(target: number, active: boolean, reduced: boolean, durationMs = 1100) {
  const [value, setValue] = useState(reduced ? target : 0);
  const fromRef = useRef(0);
  useEffect(() => {
    if (!active) return;
    if (reduced) {
      setValue(target);
      fromRef.current = target;
      return;
    }
    const from = fromRef.current;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = from + (target - from) * eased;
      setValue(v);
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      fromRef.current = target;
    };
  }, [target, active, reduced, durationMs]);
  return value;
}

// --- Chart ------------------------------------------------------------------------

const CHART_H_FULL = 260;
const PAD_FULL = { top: 16, right: 56, bottom: 28, left: 40 };
// Phones put the end label above the dot (not beside it), so the line runs nearly edge to edge.
const PAD_COMPACT = { top: 28, right: 8, bottom: 24, left: 4 };

/** Round tick step for the units axis (1/2/5 × 10^n), aiming for ~4 gridlines. */
function niceStep(range: number) {
  const raw = range / 4;
  const pow = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / pow;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * pow;
}

function CumulativeChart({
  tier,
  drawn,
  reduced,
  compact = false,
}: {
  tier: ResultsTier;
  drawn: boolean;
  reduced: boolean;
  /** Phones: shorter, no units-axis labels (the end label + zero line carry it). */
  compact?: boolean;
}) {
  const CHART_H = compact ? 170 : CHART_H_FULL;
  const PAD = compact ? PAD_COMPACT : PAD_FULL;
  const gradientId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Day 0 = zero before the first slate, so the line starts on the baseline.
  const points = useMemo(() => [{ date: '', units: 0 }, ...tier.curve], [tier]);

  const geo = useMemo(() => {
    if (width === 0) return null;
    const values = points.map((p) => p.units);
    const step = niceStep(Math.max(...values) - Math.min(0, ...values));
    const yMin = Math.min(0, Math.floor(Math.min(...values) / step) * step);
    const yMax = Math.ceil(Math.max(...values) / step) * step;
    const innerW = width - PAD.left - PAD.right;
    const innerH = CHART_H - PAD.top - PAD.bottom;
    const x = (i: number) => PAD.left + (i / (points.length - 1)) * innerW;
    const y = (u: number) => PAD.top + (1 - (u - yMin) / (yMax - yMin)) * innerH;
    const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.units).toFixed(1)}`).join('');
    const area = `${line}L${x(points.length - 1).toFixed(1)},${y(yMin).toFixed(1)}L${x(0).toFixed(1)},${y(yMin).toFixed(1)}Z`;
    const ticks: number[] = [];
    for (let v = yMin; v <= yMax + 1e-9; v += step) ticks.push(v);
    // X labels: first slate, first day of each later month present.
    const xLabels: { i: number; label: string }[] = [];
    let lastMonth = '';
    points.forEach((p, i) => {
      if (!p.date) return;
      const month = p.date.slice(0, 7);
      if (month !== lastMonth) {
        // The first slate is launch night.
        xLabels.push({ i, label: i === 1 ? 'Launch' : fmtDate(p.date) });
        lastMonth = month;
      }
    });
    return { x, y, line, area, ticks, xLabels, innerW };
  }, [points, width, CHART_H, PAD]);

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    if (!geo) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    const i = Math.round(frac * (points.length - 1));
    setHover(Math.max(1, Math.min(points.length - 1, i)));
  };

  const last = points.length - 1;
  const hovered = hover != null ? points[hover] : null;
  const dayChange = hover != null ? points[hover].units - points[hover - 1].units : 0;

  return (
    <div ref={wrapRef} className="relative w-full" style={{ height: CHART_H }}>
      {geo && (
        <svg
          width={width}
          height={CHART_H}
          className="block overflow-visible"
          role="img"
          aria-label={`Cumulative units, ${tier.label}: ${fmtUnits(tier.units)} over ${tier.days} game days`}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--brand-500)" stopOpacity="0.28" />
              <stop offset="100%" stopColor="var(--brand-500)" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Recessive grid + units axis. */}
          {geo.ticks.map((t) => (
            <g key={t} className="text-fg">
              <line
                x1={PAD.left}
                x2={PAD.left + geo.innerW}
                y1={geo.y(t)}
                y2={geo.y(t)}
                stroke="currentColor"
                strokeOpacity={t === 0 ? 0.28 : 0.08}
                strokeDasharray={t === 0 ? undefined : '2 4'}
              />
              {!compact && (
              <text
                x={PAD.left - 8}
                y={geo.y(t)}
                dy="0.32em"
                textAnchor="end"
                className="fill-current text-[10px] tabular-nums"
                fillOpacity={0.5}
              >
                {t > 0 ? `+${t}` : t}u
              </text>
              )}
            </g>
          ))}
          {geo.xLabels.map(({ i, label }) => (
            <text
              key={i}
              x={geo.x(i)}
              y={CHART_H - 8}
              textAnchor={i === 1 ? 'start' : 'middle'}
              className="fill-current text-fg text-[10px]"
              fillOpacity={0.5}
            >
              {label}
            </text>
          ))}

          {/* Area + line draw in left→right on first view; tier switches cross-fade. */}
          <g
            key={tier.label}
            style={{
              clipPath: drawn || reduced ? 'inset(0 0 0 0)' : 'inset(0 100% 0 0)',
              transition: reduced ? undefined : 'clip-path 1.6s cubic-bezier(0.33, 1, 0.68, 1)',
            }}
            className={cn(!reduced && 'animate-in fade-in duration-500')}
          >
            <path d={geo.area} fill={`url(#${gradientId})`} />
            <path
              d={geo.line}
              fill="none"
              stroke="var(--brand-500)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {/* End marker + direct label. */}
            <circle
              cx={geo.x(last)}
              cy={geo.y(points[last].units)}
              r={4.5}
              fill="var(--brand-500)"
              stroke="hsl(var(--bg))"
              strokeWidth={2}
            />
            <text
              x={compact ? geo.x(last) + 4 : geo.x(last) + 9}
              // Phones: pinned above the top gridline (clears a peak higher than the close).
              y={compact ? PAD.top - 12 : geo.y(points[last].units)}
              dy={compact ? undefined : '0.32em'}
              textAnchor={compact ? 'end' : undefined}
              className="fill-current text-fg text-[11px] font-semibold tabular-nums"
            >
              {fmtUnits(points[last].units)}
            </text>
          </g>

          {/* Crosshair. */}
          {hover != null && (
            <g className="text-fg" pointerEvents="none">
              <line
                x1={geo.x(hover)}
                x2={geo.x(hover)}
                y1={PAD.top}
                y2={CHART_H - PAD.bottom}
                stroke="currentColor"
                strokeOpacity={0.25}
              />
              <circle
                cx={geo.x(hover)}
                cy={geo.y(points[hover].units)}
                r={4}
                fill="var(--brand-500)"
                stroke="hsl(var(--bg))"
                strokeWidth={2}
              />
            </g>
          )}

          {/* Hit target spans the plot, bigger than the line. */}
          <rect
            x={PAD.left}
            y={0}
            width={geo.innerW}
            height={CHART_H}
            fill="transparent"
            onPointerMove={onMove}
            onPointerDown={onMove}
            onPointerLeave={() => setHover(null)}
            style={{ touchAction: 'pan-y' }}
          />
        </svg>
      )}

      {/* Tooltip. */}
      {geo && hovered && hover != null && (
        <div
          className={cn(
            GLASS_CARD,
            'pointer-events-none absolute z-10 px-3 py-2 text-xs whitespace-nowrap backdrop-blur-md',
          )}
          style={{
            left: Math.min(Math.max(geo.x(hover), 70), width - 70),
            top: Math.max(geo.y(hovered.units) - 64, 0),
            transform: 'translateX(-50%)',
          }}
        >
          <p className="font-medium text-fg/60">{fmtDate(hovered.date, { weekday: 'short', month: 'short', day: 'numeric' })}</p>
          <p className="mt-0.5 font-semibold text-fg tabular-nums">{fmtUnits(hovered.units)} total</p>
          <p className={cn('tabular-nums', dayChange >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')}>
            {fmtUnits(dayChange)} that day
          </p>
        </div>
      )}
    </div>
  );
}

// --- Section ----------------------------------------------------------------------

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-[var(--glass-border)] bg-fg/[0.02] px-3.5 py-3">
      <p className="text-[10px] font-semibold tracking-[0.14em] uppercase text-fg/55">{label}</p>
      <p className="mt-1 text-xl md:text-2xl font-bold tracking-tight text-fg tabular-nums">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-fg/55 tabular-nums">{sub}</p>}
    </div>
  );
}

/** Phone stat: unboxed, label under value, for a single three-column row. */
function InlineStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col justify-end px-2 text-center first:pl-0 last:pr-0">
      <p className={cn('whitespace-nowrap font-bold tracking-tight text-fg tabular-nums', value.length > 8 ? 'text-base' : 'text-lg')}>
        {value}
      </p>
      <p className="mt-0.5 text-[11px] text-fg/55">{label}</p>
    </div>
  );
}

export function NbaResultsSection({ compact = false }: { compact?: boolean }) {
  const [tierKey, setTierKey] = useState<TierKey>('edge10');
  const [snapshotKey, setSnapshotKey] = useState<SnapshotKey>('lpu');
  const snapshot = NBA_RESULTS.snapshots[snapshotKey];
  const tier = snapshot.tiers[tierKey];
  const reduced = usePrefersReducedMotion();
  const isPhone = !useMediaQuery('(min-width: 640px)', false);
  const { ref, inView } = useInView<HTMLDivElement>();
  const units = useCountUp(tier.units, inView, reduced);

  const isEdge10 = tierKey === 'edge10';
  const record = `${fmtInt(tier.wins)}–${fmtInt(tier.losses)}`;
  // Third stat: beat-the-close on the 10%+ tier only (negative CLV on all +EV stays off).
  // 30 min before tip has no CLV (it is minutes from the close) — win rate instead.
  const isMin30 = snapshotKey === 'min30';
  const winRate = `${(tier.winRate * 100).toFixed(1)}%`;
  const pushes = `${fmtInt(tier.pushes)} ${tier.pushes === 1 ? 'push' : 'pushes'}`;
  const third = isMin30
    ? { label: 'Win rate', value: winRate, sub: `${fmtInt(tier.wins + tier.losses)} decided picks` }
    : isEdge10 && tier.beatClose != null
      ? { label: 'Beat the close', value: `${Math.round(tier.beatClose * 100)}%`, sub: tier.clv != null ? `${fmtPct(tier.clv)} avg CLV` : undefined }
      : { label: 'Avg edge', value: fmtPct(tier.avgEv), sub: 'at the posted price' };

  return (
    <AnimatedSection id="results" className={cn('scroll-mt-16 md:scroll-mt-20', compact ? 'py-10 md:py-16' : 'py-12 md:py-20')}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl space-y-3 sm:space-y-4">
          <SectionEyebrow>Results · {NBA_RESULTS.label}</SectionEyebrow>
          <SectionTitle>
            {/* Phones: a short one-line headline (the full sentence needs ~580px at this size). */}
            <span className="sm:hidden whitespace-nowrap text-[26px]">
              {isEdge10 ? 'Top edges: ' : 'All +EV: '}
              <span className="text-emerald-600 dark:text-emerald-400">
                {isEdge10 ? `${fmtPct(tier.roi)} ROI` : fmtUnits(tier.units, 0)}
              </span>
            </span>
            <span className="hidden sm:inline">
              {isEdge10 ? (
                <>
                  Our biggest edges returned{' '}
                  <span className="text-emerald-600 dark:text-emerald-400">{fmtPct(tier.roi)} ROI</span>
                </>
              ) : (
                <>
                  <span className="text-emerald-600 dark:text-emerald-400">{fmtUnits(tier.units, 0)}</span> on
                  every +EV pick
                </>
              )}
            </span>
          </SectionTitle>
          <p className="text-base font-medium text-fg/70 leading-relaxed">
            <span className="sm:hidden">Every pick since launch.</span>
            <span className="hidden sm:inline">
              Every NBA pick we&apos;ve posted since our launch, through the Finals. Start with our
              10%+ edges, or open it up to every +EV pick.
            </span>
          </p>
        </div>

        <div ref={ref} className={cn(GLASS_CARD, 'mt-6 sm:mt-8 md:mt-10')}>
          {/* Header: tier + grading-moment filters (one row on phones, short labels) + window. */}
          <div className="flex items-center gap-2 sm:gap-3 sm:justify-between px-3 sm:px-5 py-3 border-b border-[var(--glass-border)]">
            <div
              role="radiogroup"
              aria-label="Which picks to show"
              className="flex flex-1 sm:flex-none sm:inline-flex rounded-full border border-[var(--glass-border)] bg-fg/[0.03] p-1"
            >
              {TIER_ORDER.map((key) => {
                const t = snapshot.tiers[key];
                const active = key === tierKey;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setTierKey(key)}
                    className={cn(
                      'flex-1 sm:flex-none whitespace-nowrap rounded-full px-2 sm:px-3 py-1.5 text-xs font-semibold transition-colors',
                      active
                        ? 'bg-blue-600 text-white dark:bg-blue-500 shadow-sm'
                        : 'text-fg/65 hover:text-fg',
                    )}
                  >
                    <span className="sm:hidden">{TIER_SHORT[key]}</span>
                    <span className="hidden sm:inline">{t.label}</span>
                    <span className={cn('hidden sm:inline ml-1.5 font-medium tabular-nums', active ? 'text-white/75' : 'text-fg/45')}>
                      {fmtInt(t.picks)}
                    </span>
                  </button>
                );
              })}
            </div>
            <div
              role="radiogroup"
              aria-label="When picks are graded"
              className="flex flex-1 sm:flex-none sm:inline-flex rounded-full border border-[var(--glass-border)] bg-fg/[0.03] p-1"
            >
              {SNAPSHOT_ORDER.map((key) => {
                const active = key === snapshotKey;
                return (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setSnapshotKey(key)}
                    className={cn(
                      'flex-1 sm:flex-none whitespace-nowrap rounded-full px-2 sm:px-3 py-1.5 text-xs font-semibold transition-colors',
                      active ? 'bg-fg/10 text-fg shadow-sm' : 'text-fg/65 hover:text-fg',
                    )}
                  >
                    <span className="sm:hidden">{SNAPSHOT_SHORT[key]}</span>
                    <span className="hidden sm:inline">{NBA_RESULTS.snapshots[key].label}</span>
                  </button>
                );
              })}
            </div>
            <p className="hidden lg:block ml-auto text-[10px] font-semibold tracking-[0.14em] uppercase text-fg/55">
              Since launch · {tier.days} game days
            </p>
          </div>

          {/* Phones stack number → chart → stat row; lg puts number + tiles left, chart right. */}
          <div className="grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-8 gap-y-4 sm:gap-y-6 px-4 sm:px-5 py-4 sm:py-5 md:py-6">
            <div className="lg:col-start-1 lg:row-start-1">
              <p className="hidden sm:block text-[10px] font-semibold tracking-[0.14em] uppercase text-fg/55">Profit</p>
              <p className="sm:mt-1 text-5xl md:text-6xl font-bold tracking-tight text-emerald-600 dark:text-emerald-400 tabular-nums">
                {fmtUnits(units)}
              </p>
              <p className="mt-1 text-sm text-fg/60">
                on {fmtInt(tier.picks)} picks{isEdge10 ? ' rated 10%+ EV' : ' rated +EV'}
              </p>
            </div>

            <div className="min-w-0 lg:col-start-2 lg:row-start-1 lg:row-span-2">
              <p className="hidden sm:block mb-2 text-[10px] font-semibold tracking-[0.14em] uppercase text-fg/55">
                Cumulative units · {tier.label} · {snapshot.label}
              </p>
              <CumulativeChart tier={tier} drawn={inView} reduced={reduced} compact={isPhone} />
            </div>

            {/* Phones: one unboxed row of three. */}
            <div className="sm:hidden grid grid-cols-3 divide-x divide-[var(--glass-border)] border-t border-[var(--glass-border)] pt-4">
              <InlineStat label="ROI" value={fmtPct(tier.roi)} />
              <InlineStat label="Record" value={record} />
              <InlineStat label={third.label} value={third.value} />
            </div>

            {/* sm+: tiles. */}
            <div className="hidden sm:grid grid-cols-4 lg:grid-cols-2 gap-2.5 lg:col-start-1 lg:row-start-2 lg:self-start">
              <StatTile label="ROI" value={fmtPct(tier.roi)} sub="per unit risked" />
              <StatTile label="Record" value={record} sub={isMin30 ? pushes : `${winRate} win rate`} />
              <StatTile label={third.label} value={third.value} sub={third.sub} />
              <StatTile
                label="Winning days"
                value={`${tier.winningDays} of ${tier.days}`}
                sub={`${Math.round((tier.winningDays / tier.days) * 100)}% of slates`}
              />
            </div>
          </div>

          {/* Table view for screen readers. */}
          <table className="sr-only">
            <caption>Cumulative units by game day, {tier.label}</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Cumulative units</th>
              </tr>
            </thead>
            <tbody>
              {tier.curve.map((p) => (
                <tr key={p.date}>
                  <td>{fmtDate(p.date)}</td>
                  <td>{fmtUnits(p.units)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="border-t border-[var(--glass-border)] px-4 sm:px-5 py-2.5 text-[11px] leading-relaxed text-fg/50">
            <span className="sm:hidden">
              Past results don&apos;t guarantee future returns.
            </span>
            <span className="hidden sm:inline">
              {`Tracked results from ${fmtInt(snapshot.tiers.all.picks)} graded NBA picks, from launch through the 2026 Finals. Each pick graded at the best posted price ${snapshot.moment}. Past results don't guarantee future returns.`}
            </span>
          </p>
        </div>
      </div>
    </AnimatedSection>
  );
}
